/*
 * Copyright (c) 2019. Nedaware SL
 */

/**
 * Nedaware S.L.
 * @author David Campos Rodríguez <david.campos.r96@gmail.com>
 */
import {Resource} from "../../api/resource";
import {Type} from "@angular/core";
import {ComponentType} from "@angular/cdk/portal";
import {BaseDialog} from "../../basic-entity-front/basic-entity-table/base-dialog";
import {BasicEntityInterface} from "../basic-entity-interface/basic-entity-interface";
import {BaseDialogData} from "../../basic-entity-front/basic-entity-table/base-dialog-data";
import {ErrorCaster} from "../casters/error-caster";
import {IRI_PROPERTY, TYPE_PROPERTY} from "../basic-entity-interface/mapping-internal";
import {ValidatorFn} from "@angular/forms";
import {TypeStr} from "./type-str";
import {PropertyTypeInterface} from "./property-type-interface";

export enum NestedBehavior {
    /**
     * Allows to pick existent elements for the nested property
     */
    PickOnly,
    /**
     * Allows only to create and remove elements for the nested property
     */
    CreateOnly,
    /**
     * The nested object will behave as PickOnly and be sent to the API just as an URI (changes would be lost)
     */
    SendIriOnly,

    CreateAndPick
}

export interface NestedModelTypeInterface<T extends Resource> {
    readonly modelType: Type<T>;
    readonly mappedBy?: string;
    readonly behavior: NestedBehavior;
    readonly creationDialog: ComponentType<BaseDialog<T>> | null;
    readonly excludeProperties: string[];

    /**
     * True when the property should be excluded
     * @param p
     */
    excludeProperty(p): boolean;

    /**
     * Create a new model to add it to the array
     */
    baseDialogDataForModel(interf: BasicEntityInterface<T>,
                           model: T,
                           isNew: boolean,
                           shouldManageSaving?: boolean,
                           disabled?: boolean): BaseDialogData<T>;
}

export class NestedModelType<T extends Resource> extends PropertyTypeInterface implements NestedModelTypeInterface<T> {
    public readonly creationDialog: ComponentType<BaseDialog<T>> | null;
    public readonly excludeProperties: string[];

    public readonly expectedApiTypes: string[] = ['object'];

    constructor(public readonly modelType: Type<T>,
                public readonly mappedBy?: string,
                public readonly behavior: NestedBehavior = NestedBehavior.PickOnly,
                properties?: string[],
                creationDialog?: ComponentType<BaseDialog<T>>
    ) {
        super();
        this.stringCaster = new ErrorCaster('StringCaster for NestedModel should be replaced in the interface generation');
        this.creationDialog = creationDialog || null;
        this.excludeProperties = properties || [IRI_PROPERTY, TYPE_PROPERTY];
    }

    public excludeProperty(p): boolean {
        return p.modelKey !== this.mappedBy &&
            this.excludeProperties.indexOf(p.modelKey) < 0;
    }

    /**
     * Create a new model to add it to the array
     */
    public baseDialogDataForModel(interf: BasicEntityInterface<T>,
                                  model: T,
                                  isNew,
                                  shouldManageSaving = false,
                                  disabled = false) {
        const cols = Object.values(interf.mappingModelToApi)
            .filter(this.excludeProperty.bind(this)).sort((a, b) => {
                if (a.order < b.order) {
                    return -1;
                } else if (a.order > b.order) {
                    return 1;
                } else {
                    return 0;
                }
            });
        return {
            model: model,
            allowIdEdition: !interf.autogeneratedId,
            columns: cols,
            showSaveAndCancelButtons: !interf.readonlyInterface,
            isNew: isNew,
            shouldManageSaving: shouldManageSaving,
            allFieldsDisabled: disabled
        };
    }

    public get validators(): ValidatorFn[] | ValidatorFn | null {
        return null;
    }

    toString(): TypeStr {
        return TypeStr.NestedModel;
    }

    asNestedModel(): NestedModelTypeInterface<any> {
        return this as NestedModelTypeInterface<any>;
    }
}
